import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { Heading4Style, Heading5Style } from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { MessagePart } from 'tripPlanner/components/Chat/messageTypes'
import type { DrawerSessionPair } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useDrawerAskAI'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import { preprocessChatMarkdown } from 'tripPlanner/utils/chatMarkdown'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { getChatLinkContentKey, useChatLinkInteractions } from 'tripPlanner/components/Chat/useChatLinkInteractions'
import PillsList from 'tripPlanner/components/Chat/PillsList'
import { isStreamedPillsListUiComponentData } from 'tripPlanner/utils/streamedProgressiveUi'
function getTextContent(message: MessageItem): string {
  return message.parts
    .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === 'text')
    .map(part => part.content)
    .join('')
}

function getUserDisplayText(message: MessageItem): string {
  return stripContextTags(getTextContent(message) || message.rawContent || '')
}

function getAssistantDisplayText(message: MessageItem): string {
  return stripContextTags(getTextContent(message))
}

function getPillsParts(message: MessageItem): Array<Extract<MessagePart, { type: 'ui_component' }>> {
  return message.parts.filter(
    (part): part is Extract<MessagePart, { type: 'ui_component' }> => (
      part.type === 'ui_component' && part.data.type === 'pills_list'
    ),
  )
}

const BUBBLE_PADDING = 16
const EMPTY_FAILED_PLACE_QUERIES = new Set<string>()

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(${rem(8)}); }
  to { opacity: 1; transform: translateY(0); }
`

const Container = styled.div`
  animation: ${fadeIn} 0.2s ease-out;
`

const Bubble = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(16)};
  box-shadow:
    0 ${rem(10)} ${rem(15)} ${rem(-3)} rgba(0, 0, 0, 0.1),
    0 ${rem(4)} ${rem(6)} ${rem(-4)} rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const BubbleHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${rem(8)};
  padding-block-start: ${rem(8)};
  padding-inline: ${rem(12)};
`

const BubbleContent = styled.div`
  padding-block-end: ${rem(BUBBLE_PADDING)};
  padding-inline: ${rem(BUBBLE_PADDING)};
  max-height: ${rem(350)};
  overflow-y: auto;
`

const ChatMarkdownWrapper = styled.div`
  h2 {
    ${Heading4Style}
  }
  h3 {
    ${Heading5Style}
  }
`

const UserMessageRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const UserBubble = styled.div`
  max-width: 75%;
  padding-block: ${rem(8)};
  padding-inline: ${rem(18)};
  border-radius: ${rem(18)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  overflow-wrap: break-word;
  white-space: pre-wrap;
`

const AssistantMessageRow = styled.div``

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`

const LoadingDots = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(4)};
  padding-block: ${rem(4)};

  span {
    width: ${rem(6)};
    height: ${rem(6)};
    border-radius: 50%;
    background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
    animation: ${pulse} 1.4s infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`

interface Props {
  latestResponse: MessageItem | undefined
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
  onOpenFullChat?: () => void
  onSendMessage?: (message: string) => void
}

function DrawerAskAIBubble({
  latestResponse,
  sessionHistory,
  isResponseStreaming,
  isWaitingForResponse,
  onOpenFullChat = noop,
  onSendMessage = noop,
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const [isClosed, setIsClosed] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const markdownContainerRef = useRef<HTMLDivElement>(null)

  const openFullChatLabel = intl.formatMessage({
    id: '7gT37mFbFhgaYeK7tqhQ',
    defaultMessage: 'Open full chat',
    description: 'Link that opens the full Trip Planner chat tab from a floating Ask AI preview',
  })
  const closeBubbleLabel = intl.formatMessage({
    id: '3j7IQmGegtc_a7v3bKD7',
    defaultMessage: 'Close bubble',
    description: 'Aria label for button to close the AI assistant chat bubble',
  })

  const handleClose = useCallback(() => {
    setIsClosed(true)
  }, [])

  const handleOpenFullChat = useCallback(() => {
    onOpenFullChat()
  }, [onOpenFullChat])

  const handleOfferClick = useCallback((data: { offerId: string; offerType?: string }) => {
    eventBus.dispatch({ type: 'open_offer_drawer', ...data })
  }, [eventBus])

  const handlePlaceClick = useCallback((data: { googlePlaceId: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_drawer', ...data })
  }, [eventBus])

  const handlePlaceSearchClick = useCallback((data: { searchQuery: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_search_drawer', ...data })
  }, [eventBus])

  const linkContentKey = useMemo(() => {
    const parts = sessionHistory.flatMap(pair => [
      ...pair.question.parts,
      ...(pair.answer?.parts ?? []),
    ])
    if (latestResponse) {
      parts.push(...latestResponse.parts)
    }
    return getChatLinkContentKey(parts)
  }, [latestResponse, sessionHistory])

  useChatLinkInteractions({
    markdownContainerRef,
    canShowHover: false,
    contentKey: linkContentKey,
    failedPlaceQueries: EMPTY_FAILED_PLACE_QUERIES,
    onOfferClick: handleOfferClick,
    onPlaceClick: handlePlaceClick,
    onPlaceSearchClick: handlePlaceSearchClick,
  })

  useEffect(() => {
    if (isResponseStreaming && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [isResponseStreaming, latestResponse?.parts])

  const loadingIndicator = useMemo(() => (
    <LoadingDots>
      <span />
      <span />
      <span />
    </LoadingDots>
  ), [])

  const handlePillSelection = useCallback((selection: Array<string>) => {
    const label = selection[0]
    if (label) {
      onSendMessage(label)
    }
  }, [onSendMessage])

  const renderAssistantContent = useCallback((message: MessageItem) => {
    const textContent = getAssistantDisplayText(message)
    const pillsParts = getPillsParts(message)
    const hasText = Boolean(textContent.trim())

    if (!hasText && pillsParts.length === 0) {
      return loadingIndicator
    }

    return (
      <Group direction="vertical" gap={12}>
        {hasText && (
          <ChatMarkdownWrapper>
            <MarkdownRender
              content={preprocessChatMarkdown(textContent)}
              fontSize="medium"
              type="compact"
            />
          </ChatMarkdownWrapper>
        )}
        {pillsParts.map((part, index) => {
          if (part.data.type !== 'pills_list') return null
          const isStreamingPills = isStreamedPillsListUiComponentData(part.data)
            && part.data.streamingStatus !== 'complete'
          return (
            <PillsList
              key={`${message.id}-pills-${index}`}
              options={part.data.options}
              select={part.data.select}
              onSelectionChange={handlePillSelection}
              isStreaming={isStreamingPills}
            />
          )
        })}
      </Group>
    )
  }, [handlePillSelection, loadingIndicator])

  if (isClosed || (!isWaitingForResponse && !latestResponse && sessionHistory.length === 0)) {
    return null
  }

  return (
    <Container data-testid="drawer-ask-ai-bubble">
      <Bubble>
        <BubbleHeader>
          <TextLink size="small" onClick={handleOpenFullChat}>
            {openFullChatLabel}
          </TextLink>
          <IconButton
            kind="tertiary"
            variant="dark"
            size="small"
            onClick={handleClose}
            aria-label={closeBubbleLabel}
          >
            <LineTimesIcon />
          </IconButton>
        </BubbleHeader>
        <BubbleContent ref={contentRef}>
          <div ref={markdownContainerRef}>
            <Group direction="vertical" gap={24}>
              {sessionHistory.length > 0 ? sessionHistory.map((pair) => {
                const userText = getUserDisplayText(pair.question)
                return (
                  <Group direction="vertical" gap={8} key={pair.question.id}>
                    {userText && (
                      <UserMessageRow>
                        <UserBubble>
                          <BodyText variant="medium" colour="light-foreground-alt">
                            {userText}
                          </BodyText>
                        </UserBubble>
                      </UserMessageRow>
                    )}
                    <AssistantMessageRow>
                      {pair.answer ? renderAssistantContent(pair.answer) : loadingIndicator}
                    </AssistantMessageRow>
                  </Group>
                )
              }) : (
                <AssistantMessageRow>
                  {isWaitingForResponse && loadingIndicator}
                  {latestResponse && renderAssistantContent(latestResponse)}
                </AssistantMessageRow>
              )}
            </Group>
          </div>
        </BubbleContent>
      </Bubble>
    </Container>
  )
}

DrawerAskAIBubble.displayName = 'DrawerAskAIBubble'

export default DrawerAskAIBubble
